import React, { useCallback, useEffect, useState } from "react";

import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useForm } from "react-hook-form";
import _noop from "lodash/noop";

import styled from "@emotion/styled";

import { Button } from "@/components/Buttons";
import Modal from "@/components/Modal";
import PrintContent from "@/components/Print/PrintContent";
import PrintPortal from "@/components/Print/PrintPortal";

import { DeliveryForWaybill } from "@/Models/DeliveryForWaybill";
import { DELIVERY_STATUS_9 } from "@/Models/Delivery";

import {
  useAuthStore,
  useAgPostalCodesStore,
  useDeliveriesStore,
  useModalStore,
  useSpotsStore,
} from "@/stores/hooks";

import {
  SIZE_TYPE_150_V3,
  SIZE_TYPE_150_V2,
  SIZE_TYPE_200,
  SIZE_TYPE_150_HANJIN,
} from "@/utils/Print";

const Title = styled.h4`
  margin: 0;
`;

const FormLabel = styled(Form.Label)`
  font-size: 16px;
  margin-bottom: 10px;
  font-weight: 500;
`;

const FormLabelBold = styled.span`
  font-size: 16px;
  margin-left: 10px;
`;

const ButtonWrap = styled.div`
  margin-top: 40px;
  display: flex;
  justify-content: space-between;
`;

const ButtonLeft = styled.div`
  display: flex;
  justify-content: flex-start;
`;

const ButtonRight = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 5px;
`;

const ModalButton = styled(Button)`
  padding: 2px;
`;

const NarrowRow = styled(Row)`
  margin-right: -5px;
  margin-left: -5px;
`;
const NarrowCol = styled(Col)`
  padding-right: 5px;
  padding-left: 5px;
  word-break: break-all;

  ${({ align }) => align === "right" && `text-align: right`}
`;

const Count = styled(NarrowCol)`
  text-align: right;
`;

const DeliveriesWrap = styled.div`
  max-height: 250px;
  overflow: auto;
`;

const TextHighlight = styled.span`
  color: red;
`;

const SORT = {
  ALLOCATION_GROUP_NAME: "allocationGroupName",
  CENTER_PRODUCT_NAME: "centerAndproductName",
  PRODUCT_NAME: "productName",
  RECEIPT_DATE_DESC: "receiptDateDesc",
  RECEIPT_DATE: "receiptDate",
};

// spot 조회 동시 요청 수 상한.
const SPOT_FETCH_CONCURRENCY = 5;

/**
 * items 를 limit 개씩 끊어 처리하고 입력 순서를 보존한 결과를 반환한다.
 */
async function mapWithConcurrency(items, limit, iteratee) {
  const results = new Array(items.length);
  let cursor = 0;

  const workers = new Array(Math.min(limit, items.length))
    .fill(null)
    .map(async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        results[index] = await iteratee(items[index], index);
      }
    });

  await Promise.all(workers);
  return results;
}

const DeliveriesComponent = () => {
  const { deliveriesForWaybill } = useDeliveriesStore().state;

  return (
    <>
      {deliveriesForWaybill.map((delivery, idx) => {
        return (
          <NarrowRow key={idx}>
            <NarrowCol xs={3}>{delivery.totalProductName}</NarrowCol>
            <NarrowCol xs={2} align="right">
              {(delivery.addressNotSupported ||
                delivery.status === DELIVERY_STATUS_9) && (
                <TextHighlight>(불가지역)</TextHighlight>
              )}
            </NarrowCol>
            <NarrowCol xs={5}>
              {delivery.bookId} | {delivery.receiverName}
            </NarrowCol>
            <Count>
              {idx + 1}/{deliveriesForWaybill.length}
            </Count>
          </NarrowRow>
        );
      })}
    </>
  );
};

export default function WaybillModal({ deliveries, onAfterSubmit = _noop }) {
  const { user } = useAuthStore().state;

  const { agPostalCodes } = useAgPostalCodesStore().state;
  const { ...agPostalCodesActions } = useAgPostalCodesStore();

  const { closeModal } = useModalStore();

  const { deliveriesForWaybill } = useDeliveriesStore().state;
  const { ...actions } = useDeliveriesStore();

  const { spotsCache } = useSpotsStore().state;
  const { ...spotsActions } = useSpotsStore();

  const { getValues, handleSubmit, register, reset } = useForm();

  const [showPrintWindow, setShowPrintWindow] = useState(false);

  useEffect(() => {
    reset({
      sizeType:
        user.canSelectHanjinWaybill && user.isHanjindomestic
          ? SIZE_TYPE_150_HANJIN
          : SIZE_TYPE_150_V3,
      sort: SORT.RECEIPT_DATE,
    });
  }, [user]);

  useEffect(() => {
    if (deliveries?.length > 0) {
      sortDeliveries(SORT.RECEIPT_DATE);
    }
  }, [deliveries]);

  useEffect(() => {
    fetchAgPostalCodes();
  }, []);

  const fetchAgPostalCodes = useCallback(async () => {
    if (!agPostalCodes.length) {
      try {
        await agPostalCodesActions.fetchAll();
      } catch (e) {
        console.log(e);
      }
    }
  }, [agPostalCodesActions.fetchAll, agPostalCodes]);

  const attachSpots = useCallback(
    async (targetDeliveries) => {
      // 이미 가진 캐시(전역 spotsCache + 이번에 새로 받은 것)를 합쳐서 사용
      const spotById = new Map();
      spotsCache.forEach((s) => spotById.set(s.id, s));

      // 캐시에 없는 고유 spotId 만 추출
      const missingSpotIds = [
        ...new Set(
          targetDeliveries
            .map((de) => de.spotId)
            .filter((id) => id && !spotById.has(id)),
        ),
      ];

      if (missingSpotIds.length > 0) {
        const fetched = await mapWithConcurrency(
          missingSpotIds,
          SPOT_FETCH_CONCURRENCY,
          async (id) => {
            try {
              return await spotsActions.fetchById(id);
            } catch (e) {
              console.log(e);
              return null;
            }
          },
        );

        missingSpotIds.forEach((id, i) => {
          if (fetched[i]) {
            spotById.set(id, fetched[i]);
          }
        });
      }

      targetDeliveries.forEach((de) => {
        if (de.spotId) {
          de.spot = spotById.get(de.spotId) || de.spot;
        }
      });
    },
    [spotsCache, spotsActions.fetchById],
  );

  const onSubmit = useCallback(
    async (_data) => {
      try {
        await actions.printedBulk(deliveries);

        setShowPrintWindow(true);
        onAfterSubmit();
      } catch (e) {
        window.alert(`운송장 출력에 실패하였습니다.\n${e.message}`);
      }
    },
    [deliveries, onAfterSubmit],
  );

  const handlePrintAvailableDeliveries = useCallback(async () => {
    try {
      const availableDeliveries = deliveries.filter(
        (d) => !d.addressNotSupported && d.status !== DELIVERY_STATUS_9,
      );

      if (availableDeliveries.length === 0) {
        window.alert("배송가능 지역 접수건이 없습니다.");
        return;
      }

      await sortDeliveries(getValues("sort"), availableDeliveries);
      await actions.printedBulk(availableDeliveries);
      setShowPrintWindow(true);

      onAfterSubmit();
    } catch (e) {
      window.alert(`운송장 출력에 실패하였습니다.\n${e.message}`);
    }
  }, [deliveries, actions.printedBulk, onAfterSubmit]);

  // const filterDeliveries = useCallback((deliveries) => {
  //   if (getValues("disabledPrintForNotSupportedAddress")) {
  //     return deliveries.filter((d) => d.status !== DELIVERY_STATUS_9);
  //   } else {
  //     return deliveries;
  //   }
  // }, []);

  const getSortTarget = (delivery, sort) => {
    switch (sort) {
      case SORT.CENTER_PRODUCT_NAME:
        let str = "";
        if (delivery.receiverAllocationGroupName) {
          str += delivery.receiverAllocationGroupName.slice(0, 1);
        }
        str += delivery.productName;
        return str;
      case SORT.ALLOCATION_GROUP_NAME:
        return delivery["receiverAllocationGroupName"]?.trim();
      case SORT.RECEIPT_DATE:
      case SORT.RECEIPT_DATE_DESC:
        return delivery.receiptDate;
      case SORT.PRODUCT_NAME:
        return delivery["totalProductName"]?.trim();
      default:
        return delivery[sort]?.trim();
    }
  };

  const sortDeliveries = async (sort, target = deliveries) => {
    const _deliveries = [...target].sort((a, b) => {
      const aValue = getSortTarget(a, sort);
      const bValue = getSortTarget(b, sort);

      if (sort === SORT.RECEIPT_DATE_DESC) {
        return bValue > aValue ? 1 : bValue < aValue ? -1 : 0;
      }

      return aValue > bValue ? 1 : aValue < bValue ? -1 : 0;
    });

    const _deliveriesWithSpots = [..._deliveries];

    await attachSpots(_deliveriesWithSpots);

    actions.setDeliveriesForWaybill(_deliveriesWithSpots);
  };

  const handleChangeOrder = (e) => {
    sortDeliveries(e.target.value);
  };

  const addressNotSupportedDeliveries = deliveries.filter(
    (d) => d.addressNotSupported || d.status === DELIVERY_STATUS_9,
  );

  return (
    <Modal>
      <Modal.Header closeButton={false}>
        <Title>운송장 출력</Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit(onSubmit)}>
          <Form.Group>
            <FormLabel>사이즈 선택</FormLabel>
            {user.canSelectHanjinWaybill && (
              <Form.Check
                label="100 x 150 한진 사이즈"
                type="radio"
                id="sizeType-150-hanjin"
                value={SIZE_TYPE_150_HANJIN}
                name="sizeType"
                ref={register}
              />
            )}
            <Form.Check
              label="100 x 150 사이즈 (신규)"
              type="radio"
              id="sizeType-150-v3"
              value={SIZE_TYPE_150_V3}
              name="sizeType"
              ref={register}
            />
            <Form.Check
              label="100 x 150 사이즈"
              type="radio"
              id="sizeType-150-v2"
              value={SIZE_TYPE_150_V2}
              name="sizeType"
              ref={register}
            />
            <Form.Check
              label="100 x 200 사이즈"
              type="radio"
              id="sizeType-200"
              value={SIZE_TYPE_200}
              name="sizeType"
              ref={register}
            />
          </Form.Group>
          <Form.Group>
            <FormLabel>인쇄 순서</FormLabel>
            <Form.Check
              label="접수순(기본값)"
              type="radio"
              id="sort-receiptDate"
              value={SORT.RECEIPT_DATE}
              name="sort"
              onClick={handleChangeOrder}
              ref={register}
            />
            <Form.Check
              label="접수역순"
              type="radio"
              id="sort-receiptDateDesc"
              value={SORT.RECEIPT_DATE_DESC}
              name="sort"
              onClick={handleChangeOrder}
              ref={register}
            />
            <Form.Check
              label="상품명순"
              type="radio"
              id="sort-productName"
              value={SORT.PRODUCT_NAME}
              name="sort"
              onClick={handleChangeOrder}
              ref={register}
            />
            <Form.Check
              label="센터명 + 상품명순"
              type="radio"
              id="sort-centerAndproductName"
              value={SORT.CENTER_PRODUCT_NAME}
              name="sort"
              onClick={handleChangeOrder}
              ref={register}
            />
            <Form.Check
              label="지역그룹명순"
              type="radio"
              id="sort-allocationGroupName"
              value={SORT.ALLOCATION_GROUP_NAME}
              name="sort"
              onClick={handleChangeOrder}
              ref={register}
            />
          </Form.Group>
          <Form.Group>
            <FormLabel>
              출력 배송 목록{" "}
              <FormLabelBold>
                총 {deliveries.length}건{" "}
                {addressNotSupportedDeliveries?.length > 0 && (
                  <TextHighlight>
                    (배송불가지역 접수건 {addressNotSupportedDeliveries?.length}
                    건)
                  </TextHighlight>
                )}
              </FormLabelBold>
            </FormLabel>
            <DeliveriesWrap>
              <DeliveriesComponent />
            </DeliveriesWrap>
          </Form.Group>

          <ButtonWrap>
            <ButtonLeft>
              <ModalButton
                type="button"
                width="180px"
                onClick={handlePrintAvailableDeliveries}
              >
                배송가능 지역 운송장 출력
                <br />
                (배송불가지역 제외)
              </ModalButton>
            </ButtonLeft>
            <ButtonRight>
              <ModalButton type="submit" width="120px">
                전체 출력
              </ModalButton>
              <ModalButton
                onClick={closeModal}
                type="button"
                variant="outline-secondary"
                width="120px"
              >
                취소
              </ModalButton>
            </ButtonRight>
          </ButtonWrap>
        </Form>

        {showPrintWindow && (
          <PrintPortal sizeType={getValues().sizeType}>
            <PrintContent
              deliveries={deliveriesForWaybill.map(
                (d) =>
                  new DeliveryForWaybill(
                    d,
                    getValues().sizeType,
                    agPostalCodes,
                    user.useMasking,
                    user.username,
                  ),
              )}
              sizeType={getValues().sizeType}
            />
          </PrintPortal>
        )}
      </Modal.Body>
    </Modal>
  );
}
